/*
  Aurdent — interactions (compagnon de aurdent-interactions.css)
  Chargé en « defer » sur toutes les pages. Aucune dépendance, aucun écouteur de défilement,
  aucune boucle : IntersectionObserver, MutationObserver ciblés et requestAnimationFrame.
  Chaque fonction est rejouable sur une section rechargée dans l'éditeur (shopify:section:load)
  et marque les éléments déjà branchés pour éviter les doubles initialisations.
*/
(function () {
  'use strict';

  var html = document.documentElement;
  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var level = html.getAttribute('data-am') || 'premium';

  function motionOn() {
    return !reduceQuery.matches && (level === 'premium' || level === 'subtle');
  }
  function signatureOn() {
    return motionOn() && html.getAttribute('data-am-signature') !== 'false';
  }
  function each(scope, selector, fn) {
    Array.prototype.forEach.call(scope.querySelectorAll(selector), fn);
  }
  function bindOnce(el, key) {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  }

  /* 1. Apparitions au défilement — une seule fois, uniquement sous la ligne de flottaison. */
  var REVEAL = [
    '.apdp-why', '.apdp-band .apdp-wrap', '.apdp-benefit', '.apdp-video', '.apdp-rsteps li',
    '[id^="app-"] .app-wrap', '[id^="afg-"] .afg-wrap', '.afg-item', '[id^="asp-"] .asp-wrap',
    '[id^="afq-"] .afq-wrap', '[id^="awc-"] .awc-wrap', '[id^="aft-"] .aft-social'
  ].join(',');
  var STAGGER = '.apdp-benefit, .afg-item, .apdp-rsteps li';
  var revealIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      revealIO.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  function initReveal(scope) {
    if (!revealIO || !motionOn()) return;
    var vh = window.innerHeight;
    each(scope, REVEAL, function (el, i) {
      if (!bindOnce(el, 'amReveal')) return;
      if (el.getBoundingClientRect().top < vh) return; // déjà visible : jamais masqué
      el.classList.add('am-reveal');
      if (el.matches(STAGGER)) el.style.setProperty('--am-delay', (i % 4) * 60 + 'ms');
      revealIO.observe(el);
    });
  }

  /* 2. Accordéons — ouverture et fermeture en déroulé, sur l'élément <details> natif (clavier et
     lecteurs d'écran conservés). Clics rapides : l'animation en cours est reprise là où elle est. */
  var ACCORDIONS = '.apdp-mini details, .apdp-acc details, .apdp-faq details, .apdp-offer__more, [id^="afq-"] details, [id^="aps-"] details';

  function initAccordions(scope) {
    if (!('animate' in Element.prototype)) return;
    each(scope, ACCORDIONS, function (d) {
      if (!bindOnce(d, 'amAcc')) return;
      var summary = d.querySelector(':scope > summary');
      var body = summary && summary.nextElementSibling;
      if (!body) return;
      var anim = null;
      var cleanup = function () {
        body.style.overflow = '';
        body.style.boxSizing = '';
        d.classList.remove('am-closing');
        anim = null;
      };
      summary.addEventListener('click', function (e) {
        if (!motionOn()) return; // comportement natif, instantané
        e.preventDefault();
        var closing = d.open && !(anim && anim.id === 'closing');
        // Fermé, le contenu garde sa hauteur dans Chrome récent (::details-content) : on part donc de 0.
        var start = d.open ? body.getBoundingClientRect().height : 0;
        if (anim) anim.cancel();
        body.style.overflow = 'hidden';
        body.style.boxSizing = 'border-box'; // la hauteur animée inclut les marges intérieures
        if (!closing) {
          d.classList.remove('am-closing');
          d.open = true;
          var end = body.scrollHeight;
          anim = body.animate(
            [{ height: start + 'px', opacity: start ? 1 : 0 }, { height: end + 'px', opacity: 1 }],
            { duration: 320, easing: 'cubic-bezier(.32,.72,0,1)' }
          );
          anim.id = 'opening';
          anim.onfinish = cleanup;
        } else {
          d.classList.add('am-closing');
          anim = body.animate(
            [{ height: start + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
            { duration: 240, easing: 'cubic-bezier(.2,0,0,1)' }
          );
          anim.id = 'closing';
          anim.onfinish = function () { d.open = false; cleanup(); };
        }
      });
    });
  }

  /* 3. Reflet signature : une seule fois quand un bouton d'achat clé arrive à l'écran (mobile
     compris, où il n'y a pas de survol). */
  var SHINE = '.apdp-form .apdp-btn, .apdp-sticky .apdp-btn, .aps-btn, .avd-btn:not(.avd-btn--ghost), .app-btn';
  var shineIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      var b = e.target;
      shineIO.unobserve(b);
      if (b.disabled) return;
      b.classList.add('am-shine');
      b.addEventListener('animationend', function () { b.classList.remove('am-shine'); }, { once: true });
    });
  }, { threshold: 0.9 }) : null;

  function initShine(scope) {
    if (!shineIO || !signatureOn()) return;
    each(scope, SHINE, function (b) { if (bindOnce(b, 'amShine')) shineIO.observe(b); });
  }

  /* 4. Halo qui suit le pointeur (souris uniquement) sur les grands boutons d'achat. */
  var HALO = '.apdp-form .apdp-btn, .apdp-sticky .apdp-btn, .aps-btn';
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function initHalo(scope) {
    if (!finePointer.matches || level !== 'premium' || !signatureOn()) return;
    each(scope, HALO, function (b) {
      if (!bindOnce(b, 'amHalo')) return;
      var rect = null, frame = 0, x = 0, y = 0;
      b.addEventListener('pointerenter', function () { rect = b.getBoundingClientRect(); });
      b.addEventListener('pointermove', function (e) {
        if (!rect) rect = b.getBoundingClientRect();
        x = e.clientX - rect.left;
        y = e.clientY - rect.top;
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = 0;
          b.style.setProperty('--am-x', x + 'px');
          b.style.setProperty('--am-y', y + 'px');
        });
      }, { passive: true });
      b.addEventListener('pointerleave', function () { rect = null; });
    });
  }

  /* 5. Prix : léger fondu montant quand le montant affiché change réellement. */
  // La page Cartouche anime déjà son propre prix (aps-in) : on ne l'ajoute pas ici pour éviter deux effets.
  var PRICES = '[data-apdp-price], [data-apdp-sticky-price], [data-apdp-q1-price], [data-apdp-q2-price]';

  function initPriceTick(scope) {
    if (!('MutationObserver' in window)) return;
    each(scope, PRICES, function (el) {
      if (!bindOnce(el, 'amTick')) return;
      var last = el.textContent;
      new MutationObserver(function () {
        if (el.textContent === last) return;
        last = el.textContent;
        if (!motionOn()) return;
        el.classList.remove('am-tick');
        void el.offsetWidth;
        el.classList.add('am-tick');
      }).observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  /* 6. Formulaires : indicateur de traitement sans changer la largeur du bouton, et blocage du
     double envoi. Les formulaires gérés par un script (ex. recherche de l'eau) sont suivis via
     l'état « disabled » de leur bouton ; les formulaires natifs Horizon ne sont pas touchés. */
  var FORM_SCOPES = '[id^="apdp-"], [id^="aps-"], [id^="aft-"], [id^="awc-"], [id^="acu-"]';
  var ADDED_KEY = 'aurdent:added';

  function setLoading(btn, on) {
    if (!btn) return;
    if (on) {
      if (btn.classList.contains('am-loading')) return;
      var s = document.createElement('span');
      s.className = 'am-spinner';
      s.setAttribute('aria-hidden', 'true');
      btn.appendChild(s);
      btn.classList.add('am-loading');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.classList.remove('am-loading');
      btn.removeAttribute('aria-busy');
      var sp = btn.querySelector('.am-spinner');
      if (sp) sp.remove();
    }
  }

  function setDone(btn) {
    if (!btn) return;
    setLoading(btn, false);
    var c = document.createElement('span');
    c.className = 'am-check';
    c.setAttribute('aria-hidden', 'true');
    c.innerHTML = '<svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 10.5l4 4 8-9"/></svg>';
    btn.appendChild(c);
    btn.classList.add('am-done');
    setTimeout(function () { btn.classList.remove('am-done'); c.remove(); }, 1400);
  }

  /* 6 bis. Ajout au panier sans quitter la page : même protocole que les formulaires du thème
     (événement standard Shopify + HTML des sections du panier), puis ouverture du tiroir.
     Si le tiroir est absent, désactivé, ou si le script ne peut pas se charger, l'envoi classique
     vers la page panier reprend la main. */
  function cartDrawer() {
    var c = document.querySelector('cart-drawer-component');
    return c ? c.closest('theme-drawer') : null;
  }
  function drawerAddAvailable(form) {
    return html.getAttribute('data-am-drawer') !== 'false' &&
      'fetch' in window && !!cartDrawer() &&
      /\/cart\/add/.test(form.getAttribute('action') || '') &&
      !/\/cart\/?$/.test(location.pathname);
  }
  // Shopify renvoie parfois ses erreurs en anglais : on garde un message français, clair et utile.
  function friendlyError(res) {
    var raw = (res && (res.description || res.message)) || '';
    if (/[àâçéèêëîïôûùœ’]/i.test(raw)) return raw; // déjà en français
    if (/already|maximum|quantity/i.test(raw)) return 'La quantité disponible est déjà dans votre panier.';
    if (/sold out|out of stock|not available|unavailable/i.test(raw)) return 'Ce produit n’est plus disponible pour le moment.';
    return 'Ce produit n’a pas pu être ajouté. Réessayez dans un instant.';
  }

  // Le module d'événements Shopify est chargé par un petit <script type="module"> dans theme.liquid :
  // Shopify réécrit les import() des fichiers .js servis en require(), inutilisable dans le navigateur.
  function cartEvents() {
    if (window.AurdentCartEvents) return Promise.resolve(window.AurdentCartEvents);
    return new Promise(function (resolve, reject) {
      document.addEventListener('aurdent:cart-events-ready', function () { resolve(window.AurdentCartEvents); }, { once: true });
      setTimeout(function () { reject(new Error('Module panier indisponible')); }, 4000);
    });
  }

  function ajaxAdd(form) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var fd = new FormData(form);
    var ids = [];
    each(document, 'cart-items-component', function (c) {
      if (c.dataset.sectionId && ids.indexOf(c.dataset.sectionId) < 0) ids.push(c.dataset.sectionId);
    });
    if (ids.length) {
      fd.set('sections', ids.join(','));
      fd.set('sections_url', location.pathname);
    }
    var id = String(fd.get('id'));
    var qty = Number(fd.get('quantity')) || 1;

    return cartEvents().then(function (ev) {
      var deferred = ev.CartLinesUpdateEvent.createPromise();
      form.dispatchEvent(new ev.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: id, quantity: qty }],
        promise: deferred.promise
      }));

      var headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };
      return fetch(root + 'cart/add.js', { method: 'POST', body: fd, headers: headers })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          return fetch(root + 'cart.js', { headers: headers })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var failed = !!res.status;
              deferred.resolve({
                cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: { items: cart.items, source: 'aurdent-form', itemCount: qty, sections: failed ? undefined : res.sections, didError: failed }
              });
              if (failed) {
                form.dispatchEvent(new ev.CartErrorEvent({
                  error: res.message || 'Add to cart failed',
                  code: 'INVALID',
                  detail: { description: res.description, errors: res.errors }
                }));
                throw { amMessage: friendlyError(res) };
              }
              return id;
            });
        })
        .catch(function (err) {
          if (err && err.amMessage) throw err;
          deferred.reject(err);
          throw { amMessage: 'Connexion impossible pour le moment. Réessayez dans un instant.' };
        });
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.closest(FORM_SCOPES)) return;
    var btn = e.submitter || form.querySelector('[type="submit"]');

    if (!e.defaultPrevented && drawerAddAvailable(form)) {
      e.preventDefault();
      if (form.dataset.amBusy) return; // second clic : ignoré
      form.dataset.amBusy = '1';
      var buttons = [btn];
      if (form.id) each(document, '[form="' + form.id + '"][type="submit"]', function (b) { if (buttons.indexOf(b) < 0) buttons.push(b); });
      buttons.forEach(function (b) { setLoading(b, true); });

      // Ouverture confiée au mécanisme natif du thème (« auto-open » du tiroir, testé sur mobile :
      // il ouvre, met à jour le contenu puis recalcule la mise en page). On ne l'ouvre nous-mêmes
      // qu'en secours, s'il n'est toujours pas ouvert un instant après l'ajout.
      var drawerComponent = document.querySelector('cart-drawer-component');
      if (drawerComponent) drawerComponent.setAttribute('auto-open', '');

      ajaxAdd(form).then(function (variantId) {
        setTimeout(function () {
          var drawer = cartDrawer();
          if (drawer && !drawer.isOpen) drawer.open();
        }, 900);
        buttons.forEach(function (b) { if (b === btn) setDone(b); else setLoading(b, false); });
        setTimeout(function () {
          var row = document.querySelector('cart-drawer-component .cart-items__table-row[data-key^="' + variantId + ':"]');
          if (row && motionOn()) row.classList.add('am-added');
        }, 450);
      }, function (err) {
        buttons.forEach(function (b) { setLoading(b, false); });
        if (err && err.amMessage) { toast(err.amMessage, 'error'); return; }
        // Module indisponible ou réponse illisible : envoi classique vers la page panier.
        form.submit();
      }).then(function () { delete form.dataset.amBusy; });
      return;
    }

    if (e.defaultPrevented) {
      // Formulaire piloté par un script : on suit son bouton le temps du traitement.
      if (!btn || !('MutationObserver' in window)) return;
      var mo = new MutationObserver(function () { setLoading(btn, btn.disabled); });
      mo.observe(btn, { attributes: true, attributeFilter: ['disabled'] });
      setLoading(btn, btn.disabled);
      setTimeout(function () { mo.disconnect(); setLoading(btn, false); }, 20000);
      return;
    }

    if (form.dataset.amBusy) { e.preventDefault(); return; } // second clic : ignoré
    form.dataset.amBusy = '1';
    setLoading(btn, true);
    // Un bouton d'achat hors du formulaire (barre mobile) et le bouton principal montrent le même état.
    if (form.id) each(document, '[form="' + form.id + '"][type="submit"]', function (b) { if (b !== btn) setLoading(b, true); });

    var idInput = form.querySelector('input[name="id"]');
    if (idInput && /\/cart\/add/.test(form.getAttribute('action') || '')) {
      try { sessionStorage.setItem(ADDED_KEY, JSON.stringify({ id: idInput.value, t: Date.now() })); } catch (err) {}
    }
  });

  // Retour arrière vers une page mise en cache : on rend les boutons à nouveau utilisables.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    each(document, 'form[data-am-busy]', function (f) { delete f.dataset.amBusy; });
    each(document, '.am-loading', function (b) { setLoading(b, false); });
  });

  /* 7. Confirmation d'ajout : sur la page panier, la ligne ajoutée est mise en valeur et un message
     est annoncé (fermable, disparaît seul après 5 s). */
  function toast(message, kind) {
    var isError = kind === 'error';
    var t = document.createElement('div');
    t.className = 'am-toast' + (isError ? ' am-toast--error' : '');
    t.setAttribute('role', isError ? 'alert' : 'status');
    t.innerHTML =
      (isError
        ? '<svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="10" cy="10" r="8"/><path d="M10 6v5M10 13.5v.5"/></svg>'
        : '<svg class="am-toast__check" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 10.5l4 4 8-9"/></svg>') +
      '<span></span><button type="button" aria-label="Fermer le message"><svg viewBox="0 0 12 12" width="10" height="10" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M1 1l10 10M11 1L1 11"/></svg></button>';
    t.querySelector('span').textContent = message;
    document.body.appendChild(t);
    var timer;
    var close = function () {
      clearTimeout(timer);
      if (!t.isConnected) return;
      if (!motionOn()) { t.remove(); return; }
      t.classList.add('is-leaving');
      t.addEventListener('animationend', function () { t.remove(); }, { once: true });
    };
    t.querySelector('button').addEventListener('click', close);
    timer = setTimeout(close, isError ? 8000 : 5000); // une erreur reste plus longtemps à l'écran
  }

  function confirmAdded() {
    var data;
    try { data = JSON.parse(sessionStorage.getItem(ADDED_KEY) || 'null'); sessionStorage.removeItem(ADDED_KEY); } catch (err) { return; }
    if (!data || Date.now() - data.t > 20000) return;
    var row = document.querySelector('.cart-items__table-row[data-key^="' + data.id + ':"]');
    if (!row) return; // l'ajout a échoué : Shopify affiche lui-même le message d'erreur
    row.classList.add('am-added');
    toast('Ajouté à votre panier');
  }

  /* 8. Pastille du panier : petite pulsation quand le nombre d'articles change. */
  function initCartBubble() {
    var header = document.querySelector('#header-component') || document.querySelector('header');
    if (!header || !('MutationObserver' in window)) return;
    var readCount = function () {
      var c = header.querySelector('.cart-bubble__text-count');
      return c ? c.textContent.trim() : '';
    };
    var last = readCount();
    new MutationObserver(function () {
      var now = readCount();
      if (now === last) return;
      last = now;
      var bubble = header.querySelector('.cart-bubble');
      if (!bubble || !motionOn()) return;
      bubble.classList.remove('am-pop');
      void bubble.offsetWidth;
      bubble.classList.add('am-pop');
    }).observe(header, { childList: true, characterData: true, subtree: true });
  }

  /* 9. Chiffres (dureté de l'eau) : défilent de 0 à leur vraie valeur quand ils arrivent à l'écran.
     La vraie valeur est dans le HTML (moteurs de recherche, sans JS) ; seuls les chiffres encore
     sous la ligne de flottaison sont animés, une seule fois. */
  var COUNT = '.afg-value';
  var countIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      countIO.unobserve(e.target);
      runCount(e.target);
    });
  }, { threshold: 0.6 }) : null;

  function countNode(el) {
    var n = el.firstChild;
    return n && n.nodeType === 3 && /\d/.test(n.data) ? n : null;
  }
  function runCount(el) {
    var node = countNode(el);
    if (!node) return;
    var target = parseFloat(el.dataset.amCount);
    var decimals = +el.dataset.amDecimals;
    var fmt = function (v) { return v.toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }); };
    var t0 = null, duration = 1100;
    var step = function (now) {
      if (t0 === null) t0 = now;
      var p = Math.min(1, (now - t0) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      node.data = fmt(target * eased);
      if (p < 1) requestAnimationFrame(step);
      else node.data = el.dataset.amFinal; // texte d'origine exact
    };
    requestAnimationFrame(step);
  }
  function initCountUp(scope) {
    if (!countIO || !motionOn()) return;
    var vh = window.innerHeight;
    each(scope, COUNT, function (el) {
      if (!bindOnce(el, 'amCountBound')) return;
      var node = countNode(el);
      if (!node || el.getBoundingClientRect().top < vh) return;
      var raw = node.data.trim();
      var value = parseFloat(raw.replace(/\s/g, '').replace(',', '.'));
      if (isNaN(value)) return;
      var dec = raw.indexOf(',') > -1 ? raw.split(',')[1].replace(/\D/g, '').length : 0;
      el.dataset.amCount = value;
      el.dataset.amDecimals = dec;
      el.dataset.amFinal = node.data;
      node.data = (0).toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
      countIO.observe(el);
    });
  }

  /* Démarrage + éditeur de thème */
  function init(scope) {
    initCountUp(scope);
    initReveal(scope);
    initAccordions(scope);
    initShine(scope);
    initHalo(scope);
    initPriceTick(scope);
  }

  function start() {
    init(document);
    initCartBubble();
    if (document.querySelector('.cart-items__table-row')) confirmAdded();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  // Dans l'éditeur, une section masquée par une apparition est rendue visible dès qu'on la sélectionne.
  document.addEventListener('shopify:section:select', function (e) {
    each(e.target, '.am-reveal', function (el) { el.classList.add('is-in'); });
  });
})();
